// ── AllLoansPage ──
import { Fragment, useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, fmt, fmtDate, localFilter } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, Icon, PageLink, Pagination, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { usePagination, useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const AllLoansPage = ({ fundname }) => {
    const { api, role, view, go, rowNav, setModal, setConfirm, delRec } = useApp();
  const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
  const [q,setQ]=useState(fundname || "");
  const [terms,setTerms]=useState([]);
  const load=()=>{setLoading(true);api.get("getLoans").then(d=>{setRows(d);setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});
    api.get("getTerms").then(d=>setTerms(Array.isArray(d)?d:[])).catch(()=>setTerms([]));};
  useEffect(()=>{load();},[]);

  const filtered = localFilter(rows,q,["loannumber","companyname","loanstatus","facilitytype","collateraltype","fundnames"])
    .filter(l => !["Terminated","Funding in Progress"].includes(l.loanstatus));

  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'loannumber');
  const { paginated:displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

  useEffect(()=>{ setQ(fundname || ""); reset(); },[fundname]);

  const groupOrder = ["Accrual","Non Accrual","Charged Off","Paid Off"];
  const groups = groupOrder.map(status=>({
    status,
    rows: sorted.filter(l=>l.loanstatus===status)
  })).filter(g=>g.rows.length>0);

  const loanTotals = arr => ({
    count:      arr.length,
    commitment: arr.reduce((s,l)=>s+(parseFloat(l.commitmentamount)||0),0),
    bod:        arr.reduce((s,l)=>s+(parseFloat(l.loanbalancebod)||0),0),
    collateral:        arr.reduce((s,l)=>s+(parseFloat(l.ea_totalcollateralvalue)||0),0),
    eod:        arr.reduce((s,l)=>s+(parseFloat(l.loanbalanceeod)||0),0),
    aeq:        arr.reduce((s,l)=>s+(parseFloat(l.bsloanbalanceeod)||0),0),
    excessavail:        arr.reduce((s,l)=>s+(parseFloat(l.excessavailability)||0),0),
  });
  const grand = loanTotals(sorted);
  const COLS = 12;

  // ── Funding in Progress worklist — term-level detail (loan balances are always zero here).
  //    Loans with no term yet still appear so they're reachable to move toward funding. ──
  const fipLoans = localFilter(rows,q,["loannumber","companyname","facilitytype","collateraltype","fundnames"])
    .filter(l => l.loanstatus === "Funding in Progress");
  const fipTermsByLoan = terms.reduce((m,t)=>{ (m[t.loanid]=m[t.loanid]||[]).push(t); return m; },{});
  // A row here is one of TWO things, and the actions on it have to know which.
  // Most rows are a TERM — the thing being negotiated toward funding. A loan
  // with no term on it yet still appears, so it is reachable at all, and that
  // row is the LOAN itself. `kind` carries the difference so the pencil edits,
  // and the bin deletes, the record the row actually stands for rather than
  // whichever one is easier to reach from here.
  const fipRows = fipLoans.flatMap(l=>{
    const ts = fipTermsByLoan[l.id]||[];
    return ts.length
      ? ts.map(t=>({ key:"t"+t.id, kind:"term", rec:t, accountid:l.accountid, loanid:l.id, companyname:l.companyname, loannumber:l.loannumber, collateraltype:t.collateraltype, facility_type:t.facility_type, commitment:t.commitment, fundingamount:t.fundingamount }))
      : [{ key:"l"+l.id, kind:"loan", rec:l, accountid:l.accountid, loanid:l.id, companyname:l.companyname, loannumber:l.loannumber, collateraltype:l.collateraltype, facility_type:l.facilitytype, commitment:l.commitmentamount, fundingamount:0 }];
  });

  // The three icons, for whichever kind the row is. The eye opens the SAME
  // record the pencil edits and the bin deletes — on a term row that is the
  // terms sheet, not the loan. The loan number in the first cell is still the
  // way to the loan page, so both destinations stay reachable.
  const fipActions = (t) => t.kind === "term" ? {
    view: () => setModal({ type:"terms-view", rec:{ ...t.rec, id:t.rec.id, loanid:t.rec.loanid || t.loanid } }),
    edit: () => setModal({ type:"terms-edit", rec:{ ...t.rec, id:t.rec.id, loanid:t.rec.loanid || t.loanid }, onDone:load }),
    del:  () => setConfirm({
            msg: 'Delete these terms on loan '+t.loannumber+'?',
            onConfirm: () => delRec("Terms", t.rec.id, "Deleted terms on loan "+t.loannumber, load) }),
    viewTitle: "View terms", editTitle: "Edit terms", delTitle: "Delete these terms",
  } : {
    view: () => go("loan", { accountid:t.accountid, loanid:t.loanid, companyname:t.companyname, loannumber:t.loannumber }),
    edit: () => setModal({ type:"loan-edit", rec:{ ...t.rec, id:t.rec.id, accountid:t.rec.accountid }, onDone:load }),
    del:  () => setConfirm({
            msg: 'Delete loan '+t.loannumber+'?',
            onConfirm: () => delRec("Loans", t.rec.id, "Deleted loan "+t.loannumber, load) }),
    viewTitle: "Open loan", editTitle: "Edit loan", delTitle: "Delete this loan",
  };
  // LOAN-LEVEL DELETE, FROM THIS CARD — the same gap as the accounts page has,
  // one level down. Rows stay term-level because the detail only exists on the
  // term until funding, but the loans list below excludes "Funding in
  // Progress", so a loan that has terms had no route to the loan itself here.
  // A labelled button rather than a second bin: Icon has three types, so two
  // bins would be identical and differ only by tooltip. Once per loan, not once
  // per row.
  const fipFirstRowOfLoan = {};
  for (const t of fipRows) if (!(t.loanid in fipFirstRowOfLoan)) fipFirstRowOfLoan[t.loanid] = t.key;
  const fipShowLoanDelete = (t) => t.kind === "term" && fipFirstRowOfLoan[t.loanid] === t.key;
  const fipDeleteLoan = (t) => setConfirm({
    msg: 'Delete loan '+(t.loannumber||'')+'? This removes the loan, not just these terms.',
    onConfirm: () => delRec("Loans", t.loanid, "Deleted loan "+(t.loannumber||t.loanid), load) });

  const fipCommitmentTotal = fipRows.reduce((s,t)=>s+(parseFloat(t.commitment)||0),0);
  const fipFundingTotal    = fipRows.reduce((s,t)=>s+(parseFloat(t.fundingamount)||0),0);

  if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;
  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search loans..." />,
      <Btn key="ex" onClick={()=>exportCSV(sorted,["loannumber","companyname","loanstatus","facilitytype","collateraltype","originalfundingdate","maturitydate","commitmentamount","loanbalancebod","loanbalanceeod","bsloanbalanceeod"],"loans.csv")}>Export CSV</Btn>
    ]} />

    {/* ── Funding in Progress worklist — shows only when there are loans in funding ── */}
    {fipRows.length > 0 && (
      <Card style={{ overflowX:"auto", marginBottom:20, borderLeft:"3px solid "+B.cyan }}>
        <div style={{ fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", padding:"12px 12px 8px" }}>
          Funding in Progress — {fipRows.length} {fipRows.length===1?"item":"items"}
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
          {/* Same columns, order and cell treatment as the loans table below,
              minus the ones that carry nothing here: every row is "Funding in
              Progress" by definition, and until a loan funds it has no original
              funding date, no maturity and no balance. */}
          <thead><tr>
            <Th>Loan #</Th>
            <Th>Account</Th>
            <Th>Facility Type</Th>
            <Th>Collateral</Th>
            <ThNum>Commitment</ThNum>
            <ThNum>Funding Amount</ThNum>
            <Th></Th>
          </tr></thead>
          <tbody>
            {fipRows.length===0
              ? <tr><td colSpan={7} style={{ padding:16, textAlign:"center", color:"var(--color-text-secondary)", fontSize:13 }}>No loans in funding.</td></tr>
              : fipRows.map(t=>{
                  const act = fipActions(t);
                  return (
                  <tr key={t.key} style={{ cursor:"pointer" }}
                    {...rowNav("loan",{accountid:t.accountid,loanid:t.loanid,companyname:t.companyname,loannumber:t.loannumber})}
                    onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"}
                    onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <Td><PageLink page="loan" params={{accountid:t.accountid,loanid:t.loanid,companyname:t.companyname,loannumber:t.loannumber}} style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{t.loannumber||"—"}</PageLink></Td>
                    <Td style={{ fontWeight:500, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis" }}>
                      <PageLink page="loan" params={{accountid:t.accountid,loanid:t.loanid,companyname:t.companyname,loannumber:t.loannumber}} title={t.companyname}>{t.companyname}</PageLink>
                    </Td>
                    <Td>{t.facility_type||"—"}</Td>
                    <Td>{t.collateraltype||"—"}</Td>
                    <TdNum>{fmt(t.commitment||0)}</TdNum>
                    <TdNum>{fmt(t.fundingamount||0)}</TdNum>
                    {/* stopPropagation, or every icon also navigates: the whole
                        row carries rowNav.
                        flex-end, because this table has fewer columns than the
                        one below and its last cell is therefore wide — without
                        it the icons sit at the left of that space instead of on
                        the right edge, where the ones below them are. */}
                    <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                      <Btn onClick={act.view} title={act.viewTitle}><Icon type="view" /></Btn>
                      {role.canEdit&&<Btn onClick={act.edit} title={act.editTitle}><Icon type="edit" /></Btn>}
                      {role.canDelete&&<Btn onClick={act.del} title={act.delTitle} variant="danger"><Icon type="delete" /></Btn>}
                      {role.canDelete&&fipShowLoanDelete(t)&&
                        <Btn onClick={()=>fipDeleteLoan(t)} variant="danger"
                             title={"Delete loan "+(t.loannumber||"")+", not just these terms"}>Delete loan</Btn>}
                    </div></Td>
                  </tr>
                  );
                })}
            {fipRows.length>0 && (
              <tr style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                <td colSpan={4} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark }}>Total ({fipRows.length})</td>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(fipCommitmentTotal)}</TdNum>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(fipFundingTotal)}</TdNum>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    )}

    <Card style={{ overflowX:"auto" }}>
      <table style={{ width:"100%",borderCollapse:"collapse", whiteSpace:"nowrap" }}>
        <thead><tr>
          <STh label="Loan #"        sortKey="loannumber"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Account"       sortKey="companyname"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Status"        sortKey="loanstatus"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Facility Type" sortKey="facilitytype"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Collateral"    sortKey="collateraltype"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Orig Date"     sortKey="originalfundingdate"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Maturity Date" sortKey="maturitydate"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Commitment"    sortKey="commitmentamount"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Collateral"       sortKey="ea_totalcollateralvalue"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Loan Balance"      sortKey="loanbalanceeod"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="BS Loan Balance"    sortKey="bsloanbalanceeod" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Excess Avail"    sortKey="excessavailability" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <Th></Th>
        </tr></thead>
        <tbody>
          {groups.map(group=>{
            const sub = loanTotals(group.rows);
            // Paginate within group using displayed slice
            const groupDisplayed = displayed.filter(l=>l.loanstatus===group.status);
            if (groupDisplayed.length===0) return null;
            // A bare <> cannot carry a key, so every status band was an unkeyed
            // sibling in the same list — React warns, and a reorder can reuse
            // the wrong rows' state.
            return <Fragment key={"grp-"+group.status}>
              {/* ── Group header ── */}
              <tr key={"hdr-"+group.status}>
                <td colSpan={99} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                  <Badge s={group.status} />
                  <span style={{ marginLeft:8, fontSize:11, color:B.cyanDark, fontWeight:400 }}>{sub.count} loan{sub.count!==1?"s":""}</span>
                </td>
              </tr>

              {/* ── Group rows ── */}
              {groupDisplayed.map(l=>(
                <tr key={l.id} style={{ cursor:"pointer" }} {...rowNav("loan",{accountid:l.accountid,loanid:l.id,companyname:l.companyname,loannumber:l.loannumber})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <Td><PageLink page="loan" params={{accountid:l.accountid,loanid:l.id,companyname:l.companyname,loannumber:l.loannumber}} style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{l.loannumber}</PageLink></Td>
                  <Td style={{ fontWeight:500 }}>{l.companyname}</Td>
                  <Td><Badge s={l.loanstatus} /></Td>
                  <Td>{l.facilitytype||"—"}</Td>
                  <Td>{l.collateraltype||"—"}</Td>
                  <Td>{fmtDate(l.originalfundingdate)}</Td>
                  <Td>{fmtDate(l.maturitydate)}</Td>
                  <TdNum>{fmt(l.commitmentamount||0)}</TdNum>
                  <TdNum>{fmt(l.ea_totalcollateralvalue||0)}</TdNum>
                  <TdNum style={{ fontWeight:500 }}>{fmt(l.loanbalanceeod||0)}</TdNum>
                  <TdNum>{fmt(l.bsloanbalanceeod||0)}</TdNum>
                  <TdNum style={{ color: parseFloat(l.excessavailability||0) >= 0 ? 'inherit' : '#993C1D' }}> {fmt(l.excessavailability||0)}</TdNum>
                  <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                    <Btn onClick={()=>go("loan",{accountid:l.accountid,loanid:l.id,companyname:l.companyname,loannumber:l.loannumber})}><Icon type="view" /></Btn>
                    {role.canEdit&&<Btn onClick={()=>setModal({type:"loan-edit",rec:{...l,id:l.id,accountid:l.accountid},onDone:load})}><Icon type="edit" /></Btn>}
                    {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete loan "+l.loannumber+"?",onConfirm:()=>delRec("Loans",l.id,"Deleted loan "+l.loannumber,load)})} variant="danger"><Icon type="delete" /></Btn>}
                  </div></Td>
                </tr>
              ))}

              {/* ── Group subtotal ── */}
              <tr key={"sub-"+group.status} style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                <td colSpan={7} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, textAlign:"right" }}>
                  {group.status} Subtotal ({sub.count})
                </td>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.commitment)}</TdNum>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.collateral)}</TdNum>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.eod)}</TdNum>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.aeq)}</TdNum>
                <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.excessavail)}</TdNum>
                <td />
              </tr>
            </Fragment>;
          })}

          {/* ── Grand total ── */}
          <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
            <td colSpan={7} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
              Grand Total ({grand.count} loan{grand.count!==1?"s":""})
            </td>
            <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.commitment)}</TdNum>
            <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.collateral)}</TdNum>
            <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.eod)}</TdNum>
            <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.aeq)}</TdNum>
            <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(grand.excessavail)}</TdNum>
            <td />
          </tr>
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />

        {fundname && <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
              <span style={{ fontSize:13, color:"var(--color-text-secondary)" }}>Filtered by fund:</span>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:14, background:B.cyanLight, color:B.cyanDark, fontSize:13, fontWeight:600 }}>
                {fundname}
                <span onClick={()=>go("all-loans",{})} style={{ cursor:"pointer", fontWeight:700 }} title="Clear filter">✕</span>
              </span>
            </div>}

    </Card>
  </>;
};
